import { classNames } from '@/util/tailwindHelpers'
import { ReactNode } from 'react'
import { numberWithCommas } from '@/util/numberHelpers'
import ProductPrice from '../ProductPrice'
import Icon, { Icons } from '../Icon'
import Tooltip from '../Tooltip'
import { ScarcityInfoBlock } from './Scarcity'

export type Value =
  | {
      type: 'usdc'
      value?: number
      color?: 'green' | 'white' | 'arcade'
    }
  | {
      type: 'numeric'
      value?: number | string
      color?: 'green' | 'white' | 'arcade'
    }
  | {
      type: 'fraction'
      value?: { numerator: number | string; denominator: number | string }
    }
  | {
      type: 'meter'
      value?: number
    }

export type InfoBlockDefinition = {
  label: string | ReactNode
  value: Value
  valueIcon?: {
    name: Extract<Icons, 'trophy' | 'trophyIneligible' | 'arcade'>
    color?: 'arcade' | 'muted'
  }
  additionalInfo?: string
  appearance?: InfoBlockAppearance
}

export type InfoBlockAppearance = {
  border: '700' | '800' | 'arcade'
}

interface Props extends InfoBlockDefinition {
  className?: string
  style?: 'solo' | 'grouped'
}

const InfoBlock = ({
  className,
  label,
  value,
  valueIcon,
  additionalInfo,
  style = 'grouped',
  appearance
}: Props) => {
  appearance = { ...{ border: '800' }, ...appearance }
  const isSoloStyle = style == 'solo'
  return (
    <div
      className={classNames(
        'px-[14px] py-[11px] border-1 rounded-2xl flex flex-1 gap-[4px] align-middle relative',
        isSoloStyle ? 'flex-row' : 'flex-col',
        {
          'border-gray-700': appearance.border == '700',
          'border-gray-800': appearance.border == '800',
          'border-arcadeGreen': appearance.border == 'arcade'
        },
        className
      )}
    >
      <div className="text-gray-300 text-sm flex flex-1 justify-start align-middle curo">
        {label}
        {additionalInfo && (
          <Tooltip
            position={'top center'}
            triggerClassName="ml-[.75ch] cursor-pointer relative z-10 hover:text-white transition-colors before:content-[''] before:absolute before:-inset-1"
          >
            {additionalInfo}
          </Tooltip>
        )}
      </div>
      <div
        className={classNames('flex-1 flex items-center gap-half', {
          'justify-end': isSoloStyle
        })}
      >
        {valueIcon && (
          <Icon
            name={valueIcon.name}
            className={classNames('w-[14px] h-[14px]', {
              'text-arcadeGreen': valueIcon.color == 'arcade',
              'text-gray-300': valueIcon.color == 'muted'
            })}
          />
        )}

        {renderValue(value, style)}
      </div>
    </div>
  )
}

const renderValue = (value: Value, style: Props['style']): ReactNode => {
  switch (value.type) {
    case 'meter':
      return <ScarcityInfoBlock value={value} style={style} />
    case 'numeric':
      return (
        <div
          className={classNames('body-md', {
            'text-green': value.color == 'green',
            'text-arcadeGreen': value.color == 'arcade'
          })}
        >
          {numberWithCommas(value.value)}
        </div>
      )
    case 'fraction':
      return (
        <div className="body-md">
          {numberWithCommas(value.value.numerator)}
          <span className="text-gray-400">
            {' '}
            / {numberWithCommas(value.value.denominator)}
          </span>
        </div>
      )
    case 'usdc':
      return (
        <div className="body-md">
          {value.value ? (
            <ProductPrice
              price={value.value}
              className={classNames({
                'text-white': value.color == 'white'
              })}
              hideAndUpSuffix
            />
          ) : (
            <span className="text-gray-300">–</span>
          )}
        </div>
      )
  }
}

export default InfoBlock
